import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import { Shield, Bell, Moon, Key, ChevronLeft, User } from 'lucide-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useOptionalSidebar } from '../ui/sidebar';

const SettingsLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const securitySettingsEnabled = useFeatureFlagEnabled('security-settings');
  const apiKeyGenEnabled = useFeatureFlagEnabled('api-key-gen');
  // Only the inset shell mounts a SidebarProvider; classic desktop has none.
  const inInsetShell = Boolean(useOptionalSidebar());

  const menuItems = useMemo(() => {
    const items = [
      { icon: User, label: 'Account', path: '/settings/account' },
      { icon: Bell, label: 'Notifications', path: '/settings/notifications' },
    ];
    if (securitySettingsEnabled !== false) {
      items.push({ icon: Shield, label: 'Security', path: '/settings/security' });
    }
    if (apiKeyGenEnabled) {
      items.push({ icon: Key, label: 'API Keys', path: '/settings/api-keys' });
    }
    items.push({ icon: Moon, label: 'Appearance', path: '/settings/appearance' });
    return items;
  }, [securitySettingsEnabled, apiKeyGenEnabled]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const currentPath = location.pathname;

  // Mobile: show menu list at /settings, content at sub-routes
  if (isMobile) {
    if (currentPath === '/settings') {
      return (
        <div className="space-y-4">
          <h1 style={{ fontFamily: 'var(--font-sans)', fontSize: 22, fontWeight: 700, color: 'var(--lodge-onyx-900)' }}>
            Settings
          </h1>
          <div style={{ borderRadius: 'var(--radius-xl)', border: '1px solid var(--lodge-onyx-100)', background: 'var(--lodge-white)', overflow: 'hidden' }}>
            {menuItems.map((item, i) => (
              <Link
                key={item.path}
                to={item.path}
                className="flex items-center justify-between px-4 py-3.5 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                style={{ borderBottom: i < menuItems.length - 1 ? '1px solid var(--lodge-onyx-100)' : 'none' }}
              >
                <div className="flex items-center gap-3">
                  <item.icon size={18} style={{ color: 'var(--lodge-onyx-400)' }} />
                  <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--lodge-onyx-800)' }}>{item.label}</span>
                </div>
                <ChevronLeft size={16} style={{ color: 'var(--lodge-onyx-300)', transform: 'rotate(180deg)' }} />
              </Link>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div>
        <button
          onClick={() => navigate('/settings')}
          className="flex items-center gap-1 mb-4 transition-colors"
          style={{ fontSize: 14, color: 'var(--lodge-onyx-500)' }}
        >
          <ChevronLeft size={16} />
          Settings
        </button>
        <Outlet />
      </div>
    );
  }

  // Desktop, inset shell: the app sidebar's account menu lists these pages,
  // so the page renders content only.
  if (inInsetShell) {
    return (
      <div className="max-w-5xl">
        <Outlet />
      </div>
    );
  }

  // Desktop, classic shell: left settings list + content (the original layout).
  return (
    <div className="flex gap-8 max-w-5xl">
      <nav className="w-52 shrink-0 space-y-1">
        <h2 style={{ fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--lodge-onyx-400)', marginBottom: 12, paddingLeft: 12 }}>
          Settings
        </h2>
        {menuItems.map((item) => {
          const isActive = currentPath === item.path || currentPath.startsWith(item.path + '/');
          return (
            <Link
              key={item.path}
              to={item.path}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150"
              style={{
                background: isActive ? 'var(--nav-active-bg)' : 'transparent',
                color: isActive ? 'var(--nav-active-fg)' : 'var(--lodge-onyx-600)',
                fontWeight: isActive ? 600 : 400,
                fontSize: 14,
              }}
            >
              <item.icon size={18} style={{ color: isActive ? 'var(--nav-active-icon)' : 'var(--lodge-onyx-400)' }} />
              {item.label}
            </Link>
          );
        })}
      </nav>
      <div className="flex-1 min-w-0">
        <Outlet />
      </div>
    </div>
  );
};

export default SettingsLayout;
